import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Mail, LogIn, User, LogOut } from 'lucide-react';
import { navLinks } from '../siteContent';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

const TiktokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.12z"/>
  </svg>
);

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthed, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen ? 'bg-neutral-950/90 backdrop-blur-md border-b border-yellow-400/10' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group" aria-label="Accueil BuzzBump">
          <img
            src={LOGO_URL}
            alt="Logo BuzzBump"
            width="160"
            height="56"
            fetchpriority="high"
            className="h-12 md:h-14 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <li key={l.path}>
              <NavLink
                to={l.path}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                    isActive ? 'text-yellow-400' : 'text-neutral-200 hover:text-yellow-400'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    <span
                      className={`absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-yellow-400 transition-all duration-300 ${
                        isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                      }`}
                    />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          {isAuthed ? (
            <>
              <Link to="/mon-compte" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-yellow-400/30 bg-yellow-400/5 text-yellow-400 hover:bg-yellow-400 hover:text-neutral-900 transition-colors text-sm font-medium max-w-[220px]">
                <User size={16} />
                <span className="truncate">{user?.name || user?.email}</span>
              </Link>
              <button onClick={logout} aria-label="Déconnexion" className="h-9 w-9 grid place-items-center rounded-md text-neutral-400 hover:text-red-400 hover:bg-white/5 transition-colors">
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <Link to="/connexion" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors text-sm">
              <LogIn size={16} /> Se connecter
            </Link>
          )}
        </div>

        <button
          aria-label="Menu"
          onClick={() => setMobileOpen((o) => !o)}
          className="lg:hidden p-2 text-yellow-400"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-t border-yellow-400/10 bg-neutral-950/95 backdrop-blur-md">
          <ul className="flex flex-col px-6 py-4 gap-1">
            {navLinks.map((l) => (
              <li key={l.path}>
                <NavLink
                  to={l.path}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-md text-base font-medium ${
                      isActive ? 'text-yellow-400 bg-yellow-400/5' : 'text-neutral-200 hover:bg-white/5'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-2 border-t border-white/5 mt-2">
              {isAuthed ? (
                <>
                  <Link to="/mon-compte" className="flex items-center gap-2 px-3 py-3 rounded-md text-yellow-400 hover:bg-yellow-400/5">
                    <User size={16} /> Mon compte
                  </Link>
                  <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-3 rounded-md text-neutral-300 hover:text-red-400 hover:bg-white/5">
                    <LogOut size={16} /> Déconnexion
                  </button>
                </>
              ) : (
                <Link to="/connexion" className="flex items-center gap-2 px-3 py-3 rounded-md bg-yellow-400/10 text-yellow-400 font-medium">
                  <LogIn size={16} /> Se connecter
                </Link>
              )}
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="relative bg-neutral-950 border-t border-yellow-400/10 mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img
            src={LOGO_URL}
            alt="Logo BuzzBump"
            loading="lazy"
            className="h-16 w-auto object-contain mb-4"
          />
          <p className="text-neutral-400 max-w-md leading-relaxed">
            L'application mobile novatrice pour s'amuser sans limite. Messagerie en temps réel, système de classement, de map... Vous avez tout à redécouvrir avec BuzzBump !
          </p>
          <div className="flex items-center gap-3 mt-6">
            <a href="https://www.instagram.com/buzz_bump" target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="h-10 w-10 grid place-items-center rounded-md border border-white/10 text-neutral-300 hover:text-yellow-400 hover:border-yellow-400/40 transition-colors">
              <Instagram size={18} />
            </a>
            <a href="https://www.tiktok.com/@buzzbump" target="_blank" rel="noopener noreferrer" aria-label="TikTok"
              className="h-10 w-10 grid place-items-center rounded-md border border-white/10 text-neutral-300 hover:text-yellow-400 hover:border-yellow-400/40 transition-colors">
              <TiktokIcon className="h-4 w-4" />
            </a>
            <a href="mailto:contact.buzzbump@gmail.com" aria-label="Email"
              className="h-10 w-10 grid place-items-center rounded-md border border-white/10 text-neutral-300 hover:text-yellow-400 hover:border-yellow-400/40 transition-colors">
              <Mail size={18} />
            </a>
          </div>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Navigation</h4>
          <ul className="space-y-2">
            {navLinks.map((l) => (
              <li key={l.path}>
                <Link to={l.path} className="text-neutral-400 hover:text-yellow-400 transition-colors text-sm">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Contact</h4>
          <ul className="space-y-2 text-sm">
            <li className="text-neutral-400">Mail PRO :</li>
            <li>
              <a href="mailto:contact.buzzbump@gmail.com" className="text-yellow-400 hover:text-yellow-300 break-all">
                contact.buzzbump@gmail.com
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5 py-5 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} BuzzBump. Tous droits réservés. Qu'attendez-vous pour rejoindre la ruche ?
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-neutral-950 text-white antialiased flex flex-col">
      <Header />
      <main className="flex-1 pt-20">{children}</main>
      <Footer />
    </div>
  );
}
